import { NgModule, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';

import {
  CommonModule,
  DatePipe,
  HashLocationStrategy,
  LocationStrategy,
} from '@angular/common';
import { RoutingModule } from './modules/routing/routing.module';
import { MaterialModule } from './modules/shared/material.module';
import { AnimationModule } from './modules/shared/animation.module';
import { CustomIconsModule } from './modules/custom-icons/custom-icons.module';
import {
  LanguageManager,
  HttpLoaderFactory,
} from './managers/language/language.manager';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { ToastrModule } from 'ngx-toastr';
import { HttpClient } from '@angular/common/http';
import { AppComponent } from './app.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgxSpinnerModule } from 'ngx-spinner';
import { AutoMarginDirective } from './directives/auto-margin.directive';
import { LandingPage } from './pages/landing/landing.page';
import { LoginForcedDialog } from './dialogs/login-forced/login-forced.dialog';
import { MainPage } from './pages/main/main.page';
import { AutoLoginDirective } from './directives/login/auto-login.directive';
import { RedirectLoginDirective } from './directives/main/redirect-login.directive';
import { MainMenuComponent } from './components/main-menu/main-menu.component';
import { MenuItemComponent } from './components/menu-item/menu-item.component';
import { OrganizationSelect } from './components/select/organization/organization-select/organization.select';
import { MainHeaderComponent } from './components/main-header/main-header.component';
import { DashboardPage } from './pages/dashboard/dashboard.page';
import { SelectAutoSizerDirective } from './directives/select-auto-sizer.directive';
import { SiteSelect } from './components/select/site/site-select/site.select';
import { StatisticPage } from './pages/statistic/statistic.page';
import { CustomSelect } from './components/select/custom/custom-select/custom.select';
import { DeviceSelect } from './components/select/device/device-select/device.select';
import { ServiceSelect } from './components/select/service/service-select/service.select';
import { AccordionTable } from './components/accordion-table/accordion.table';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { MatRadioModule } from '@angular/material/radio';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { TimeDevicePage } from './pages/timestable/device/time.device.page';
import { TimeServicePage } from './pages/timestable/service/time.service.page';
import { OperatorCounterPage } from './pages/operator-counter/operator.counter.page';
import { AutocompleteComponent } from './components/autocomplete/autocomplete/autocomplete.component';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { ProgressRadio } from './components/radio-button/progress-mode/progress-mode.radio';
import { CustomRadio } from './components/radio-button/custom/custom.radio';
import { CTextCard } from './components/text-card/c.text.card';
import { MessagePage } from './pages/message/message.page';
import { AssignOperatorPage } from './pages/assign-operator/assign.operator.page';
import { CustomTableComponent } from './components/table/custom-table/custom-table.component';
import { SelectAllOption } from './components/option/select-all-option/select-all.option';
import { LocaleManager } from './managers/locale/locale.manager';
import {
  DateAdapter,
  MAT_DATE_LOCALE,
  MAT_DATE_FORMATS,
} from '@angular/material/core';
import {
  MAT_MOMENT_DATE_FORMATS,
  MomentDateAdapter,
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
} from '@angular/material-moment-adapter';
import { PasswordChangeDialg } from './dialogs/password-change/password.change.dialog';
import { ServiceManagementPage } from './pages/service-management/service.management.page';
import { OperatorManagementPage } from './pages/operator-management/operator.management.page';
import { GenericDialog } from './dialogs/generic/generic.dialog';
import { MessageDialog } from './dialogs/message/message.dialog';
import { OperatorDialog } from './dialogs/operator/operator.dialog';
import { ServiceDialog } from './dialogs/service/service.dialog';
import {
  CalendarModule,
  DateAdapter as angCalendarDateAdapter,
} from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import { TimeDialog } from './dialogs/time/time.dialog';
import { OrganizationDetailPage } from './pages/organization-detail/organization.detail.page';
import { SiteDetailPage } from './pages/site-detail/site.detail.page';
import { SiteDataContainer } from './components/data-container/site/site.data.container';
import { OrganizationDataContainer } from './components/data-container/organization/organization.data.container';
import { PlaceAutocomplete } from './components/autocomplete/place/place.autocomplete';
import { MapComponent } from './components/map/map/map.component';
import { LeafletModule } from '@asymmetrik/ngx-leaflet';
import { BookingManagementPage } from './pages/booking/booking.management.page';
import { SlotDialog } from './dialogs/slot/slot.dialog';
import { SlotPrenotationDialog } from './dialogs/slot-prenotation/slot.prenotation.dialog';
import { DeviceServiceCheckbox } from './components/checkbox/device-service/device.service.checkbox';
import { DeviceServicePage } from './pages/device-service/device.service.page';
import { ChartsComponent } from './components/charts/charts/charts.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SlotMassImportDialog } from './dialogs/slot-mass-import/slot.mass.import.dialog';
import { SiteAutocomplete } from './components/autocomplete/site/site.autocomplete';
import { LegendaComponent } from './components/legenda/legenda.component';
import { DeviceManagementPage } from './pages/device-management/device.management.page';
import { DeviceDataContainer } from './components/data-container/device/device.data.container';
import { DeviceCloneDialog } from './dialogs/device/device.clone.dialog';
import { StepperDialog } from './dialogs/stepper/stepper.dialog';
import { MatStepperModule } from '@angular/material/stepper';
import { StepperModule1 } from './components/stepper-module/module.1/module.1.component';
import { HelpAlert } from './components/help/help-alert/help.alert';
import { StepperModule2 } from './components/stepper-module/module.2/module.2.component';
import { OperatorPage } from './pages/operator/operator.page';
import { StepperModule3 } from './components/stepper-module/module.3/module.3.component';
import { DevicePage } from './pages/device/device.page';
import { CqFileComponent } from './components/cq/cq-file/cq-file.component';
import { CqFilePreviewComponent } from './components/cq/cq-file-preview/cq-file-preview.component';
import { CqImagePreviewComponent } from './components/cq/cq-file-preview/preview/cq-image-preview/cq-image-preview.component';
import { MediaOrganizationPage } from './pages/media/media-organization/media-organization.page';
import { SlotAutomatePage } from './pages/timestable/slot-automate/slot.automate.page';
import { SlotAutomateDialog } from './dialogs/slot-automate/slot.automate.dialog';
import { SlotModelPage } from './pages/timestable/slot-model/slot.model.page';
import { DaySelect } from './components/select/day/day.select';
import { SlotModelDialog } from './dialogs/slot-model/slot.model.dialog';
import { ModelApplyDialog } from './dialogs/slot-model-apply/slot.model.apply.dialog';
import { ModelSlotDialog } from './dialogs/model-slot/model.slot.dialog';
import { ModelCopyDialog } from './dialogs/slot-model-copy/slot.model.copy.dialog';
import { MediaSitePage } from './pages/media/media-site/media-site.page';
import { MediaServicePage } from './pages/media/media-service/media-service.page';
import { QuickSiteDialog } from './dialogs/quick-site/quick-site.dialog';
import { LicencePage } from './pages/licence/licence.page';
import { ClipboardDialog } from './dialogs/clipboard/clipboard.dialog';
import { SsoCallbackPage } from './pages/sso-callback/sso.callback.page';

import { Location, PathLocationStrategy } from '@angular/common';
import { DeviceStatusDialog } from './dialogs/device-status/device.status.dialog';

@NgModule({
  declarations: [
    AppComponent,
    LandingPage,
    AutoMarginDirective,
    LoginForcedDialog,
    MainPage,
    AutoLoginDirective,
    RedirectLoginDirective,
    MainMenuComponent,
    MenuItemComponent,
    OrganizationSelect,
    MainHeaderComponent,
    DashboardPage,
    SelectAutoSizerDirective,
    SiteSelect,
    StatisticPage,
    CustomSelect,
    DeviceSelect,
    ServiceSelect,
    AccordionTable,
    TimeDevicePage,
    TimeServicePage,
    OperatorCounterPage,
    AutocompleteComponent,
    ProgressRadio,
    CTextCard,
    PasswordChangeDialg,
    AssignOperatorPage,
    CustomTableComponent,
    CustomRadio,
    DeviceServicePage,
    SelectAllOption,
    DeviceServiceCheckbox,
    ServiceManagementPage,
    MessagePage,
    OperatorManagementPage,
    GenericDialog,
    MessageDialog,
    OperatorDialog,
    ServiceDialog,
    TimeDialog,
    TimeDevicePage,
    OrganizationDetailPage,
    SiteDetailPage,
    SiteDataContainer,
    PlaceAutocomplete,
    OrganizationDataContainer,
    MapComponent,
    BookingManagementPage,
    SlotDialog,
    SlotPrenotationDialog,
    ChartsComponent,
    SlotMassImportDialog,
    SiteAutocomplete,
    LegendaComponent,
    DeviceManagementPage,
    DeviceDataContainer,
    DeviceCloneDialog,
    StepperDialog,
    StepperModule1,
    StepperModule2,
    StepperModule3,
    HelpAlert,
    OperatorPage,
    DevicePage,
    CqFileComponent,
    CqFilePreviewComponent,
    CqImagePreviewComponent,
    MediaOrganizationPage,
    SlotAutomatePage,
    SlotAutomateDialog,
    SlotModelPage,
    DaySelect,
    SlotModelDialog,
    ModelApplyDialog,
    ModelSlotDialog,
    ModelCopyDialog,
    MediaSitePage,
    MediaServicePage,
    QuickSiteDialog,
    LicencePage,
    ClipboardDialog,
    DeviceStatusDialog,
    SsoCallbackPage,
  ],
  imports: [
    LeafletModule,
    RoutingModule,
    MaterialModule,
    AnimationModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatRadioModule,
    MatStepperModule,
    MatCheckboxModule,
    MatAutocompleteModule,
    CustomIconsModule,
    MatTooltipModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    ToastrModule.forRoot(),
    NgxSpinnerModule,
    NgbModule,
    CalendarModule.forRoot({
      provide: angCalendarDateAdapter,
      useFactory: adapterFactory,
    }),
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [
    DatePipe,
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    Location,
    { provide: LocationStrategy, useClass: PathLocationStrategy },
    { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(
    private languageManager: LanguageManager,
    private localeManager: LocaleManager
  ) {
    languageManager.initLanguage();
    localeManager.initLocales();
  }
}
